import React, { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { Button } from '@/components/common/UI/Button'

interface SlotOption {
  id: number
  starts_at: string
  ends_at: string
  timezone: string
  status: 'proposed' | 'accepted' | 'declined' | 'expired'
  meeting_url?: string | null
  message?: string | null
}

interface InterviewSlotPanelProps {
  applicationId: number
  employer?: boolean
}

const InterviewSlotPanel: React.FC<InterviewSlotPanelProps> = ({ applicationId, employer = false }) => {
  const queryClient = useQueryClient()
  const [times, setTimes] = useState([{ starts_at: '', ends_at: '' }])
  const [meetingUrl, setMeetingUrl] = useState('')
  const [message, setMessage] = useState('')
  const { data: slots = [], isLoading } = useQuery({
    queryKey: ['applications', applicationId, 'interview-slots'],
    queryFn: () => apiClient.get<SlotOption[]>(`/v1/applications/${applicationId}/interview-slots`),
  })
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['applications', applicationId, 'interview-slots'] })
    queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] })
    queryClient.invalidateQueries({ queryKey: ['notifications'] })
    queryClient.invalidateQueries({ queryKey: ['vacancy'] })
  }
  const propose = useMutation({
    mutationFn: () => apiClient.post(`/v1/applications/${applicationId}/interview-slots`, {
      slots: times.map((slot) => ({ starts_at: new Date(slot.starts_at).toISOString(), ends_at: new Date(slot.ends_at).toISOString() })),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Moscow',
      meeting_url: meetingUrl || null,
      message: message || null,
    }),
    onSuccess: () => { setTimes([{ starts_at: '', ends_at: '' }]); refresh() },
  })
  const respond = useMutation({
    mutationFn: (slotId: number) => apiClient.post(`/v1/interview-slots/${slotId}/respond`, null, { params: { action: 'accept' } }),
    onSuccess: refresh,
  })

  const downloadCalendar = async (slotId: number) => {
    const file = await apiClient.get<Blob>(`/v1/interview-slots/${slotId}/calendar.ics`, { responseType: 'blob' })
    const url = URL.createObjectURL(file)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = `interview-${slotId}.ics`
    anchor.click()
    URL.revokeObjectURL(url)
  }

  const updateTime = (index: number, key: 'starts_at' | 'ends_at', value: string) => {
    setTimes((current) => current.map((slot, slotIndex) => slotIndex === index ? { ...slot, [key]: value } : slot))
  }

  return (
    <section className="mt-4 border-t border-secondary-200 pt-4">
      <h4 className="font-medium text-sm text-secondary-800">Собеседование</h4>
      {isLoading ? <p className="mt-2 text-xs text-secondary-500">Загружаем варианты времени…</p> : (
        <div className="mt-2 space-y-2">
          {slots.map((slot) => (
            <div key={slot.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-secondary-50 p-3 text-sm">
              <div>
                <p className="font-medium">{new Date(slot.starts_at).toLocaleString()} – {new Date(slot.ends_at).toLocaleTimeString()}</p>
                <p className="text-xs text-secondary-500">{slot.status === 'proposed' ? 'Ожидает ответа' : slot.status === 'accepted' ? 'Подтверждено' : slot.status === 'expired' ? 'Время прошло' : 'Отклонено'} · {slot.timezone}</p>
                {slot.message && <p className="mt-1 text-xs text-secondary-600">{slot.message}</p>}
                {slot.meeting_url && <a href={slot.meeting_url} target="_blank" rel="noreferrer" className="mt-1 inline-block text-xs text-primary-700 hover:underline">Ссылка на встречу</a>}
              </div>
              {!employer && slot.status === 'proposed' && <Button size="sm" disabled={respond.isPending} onClick={() => respond.mutate(slot.id)}>Выбрать время</Button>}
              {slot.status === 'accepted' && <Button size="sm" variant="outline" onClick={() => downloadCalendar(slot.id)}>Добавить в календарь</Button>}
            </div>
          ))}
          {slots.length === 0 && <p className="text-xs text-secondary-500">Варианты времени пока не предложены.</p>}
        </div>
      )}

      {employer && slots.every((slot) => slot.status !== 'proposed') && (
        <form className="mt-3 space-y-2" onSubmit={(event) => { event.preventDefault(); propose.mutate() }}>
          {times.map((slot, index) => (
            <div key={index} className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <label className="text-xs text-secondary-600">Начало
                <input required type="datetime-local" value={slot.starts_at} onChange={(event) => updateTime(index, 'starts_at', event.target.value)} className="mt-1 w-full rounded border border-secondary-300 px-2 py-1.5 text-sm" />
              </label>
              <label className="text-xs text-secondary-600">Окончание
                <input required type="datetime-local" value={slot.ends_at} onChange={(event) => updateTime(index, 'ends_at', event.target.value)} className="mt-1 w-full rounded border border-secondary-300 px-2 py-1.5 text-sm" />
              </label>
            </div>
          ))}
          {times.length < 10 && <button type="button" className="text-xs text-primary-700 hover:underline" onClick={() => setTimes((current) => [...current, { starts_at: '', ends_at: '' }])}>+ Ещё вариант времени</button>}
          <input value={meetingUrl} onChange={(event) => setMeetingUrl(event.target.value)} placeholder="Ссылка на встречу (необязательно)" className="w-full rounded border border-secondary-300 px-3 py-2 text-sm" />
          <textarea value={message} onChange={(event) => setMessage(event.target.value)} maxLength={1000} placeholder="Комментарий кандидату (необязательно)" className="w-full rounded border border-secondary-300 px-3 py-2 text-sm" rows={2} />
          <Button type="submit" size="sm" disabled={propose.isPending}>{propose.isPending ? 'Отправляем…' : 'Предложить время'}</Button>
          {propose.isError && <p className="text-xs text-error">Не удалось предложить время. Проверьте, что слоты в будущем и не пересекаются с другими интервью.</p>}
        </form>
      )}
    </section>
  )
}

export default InterviewSlotPanel
